import { type PortfolioCoordinates, type PortfolioPowerRequest, resolvePortfolioAssets } from "./assetResolution";
import { type Portfolio, PortfolioDataSource, PortfolioType } from "./types";

export function getPortfolioEnergySupport(portfolio: Pick<Portfolio, "dataSource" | "portfolioType">) {
  return (portfolio.dataSource === PortfolioDataSource.Api || portfolio.dataSource === PortfolioDataSource.Energy) &&
    (portfolio.portfolioType === PortfolioType.Wind || portfolio.portfolioType === PortfolioType.Solar)
    ? "supported"
    : "unsupported";
}

export type PortfolioEnergyRequest = PortfolioPowerRequest;

export type PortfolioEnergyConfigurationIssue =
  | "missing-asset-id"
  | "duplicate-asset-id"
  | "invalid-power-configuration"
  | "unsupported-source"
  | "unsupported-technology";

export type ResolvedPortfolioEnergyAsset = {
  rowIndex: number;
  assetId: string | null;
  name: string;
  coordinates: PortfolioCoordinates | null;
  siteId?: string;
  configurationIssue: PortfolioEnergyConfigurationIssue | null;
  request: PortfolioEnergyRequest | null;
};

/** Resolve current portfolio rows without filtering out markers that cannot request power. */
export function resolvePortfolioEnergyAssets(portfolio: Portfolio) {
  const assets: ResolvedPortfolioEnergyAsset[] = resolvePortfolioAssets(portfolio).map((asset) => {
    let configurationIssue: PortfolioEnergyConfigurationIssue | null = asset.issue;
    if (!configurationIssue && asset.invalidFields.some((field) => field !== "coordinates")) {
      configurationIssue = "invalid-power-configuration";
    }

    return {
      rowIndex: asset.rowIndex,
      assetId: asset.assetId,
      name: asset.label,
      coordinates: asset.coordinates,
      ...(portfolio.dataSource === PortfolioDataSource.Energy && asset.siteId ? { siteId: asset.siteId } : {}),
      configurationIssue,
      request: configurationIssue ? null : asset.powerRequest,
    };
  });

  // Canonical JSON avoids hash collisions and ignores names, imported columns, and row order.
  // Time, model and request options belong to the eventual point-cache key, not this configuration fingerprint.
  const requestInputs = assets.map((asset) => [
    asset.assetId,
    asset.coordinates,
    asset.configurationIssue,
    asset.request?.parameter ?? null,
    asset.siteId ?? null,
  ]);
  requestInputs.sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
  const requestFingerprint = JSON.stringify([
    portfolio.idAccount,
    portfolio.id,
    portfolio.dataSource,
    portfolio.portfolioType,
    requestInputs,
  ]);
  return { assets, requestFingerprint };
}
